import { logWarn } from '@ptek/client-logger';
import React from 'react';
import {
  CONSENT_ACTIONS,
  DIALOG_TYPES,
  EXIT_ACTIONS,
  ExitConfirmData,
  PatientConsentData,
  SESSION_DISCARD_ACTIONS,
  SessionDiscardData,
  TELEHEALTH_INSTRUCTION_ACTIONS,
  TelehealthInstructionsData,
} from '../../../stores/dialogStore';
import { ExitConfirmDialog } from './ExitConfirmDialog';
import { PatientConsentDialog } from './PatientConsentDialog';
import { SessionDiscardDialog } from './SessionDiscardDialog';
import { TelehealthInstructionsDialog } from './TelehealthInstructionsDialog';

interface DialogRendererProps {
  activeDialogs: Map<string, any>;
  getDialogData: <
    T =
      | ExitConfirmData
      | PatientConsentData
      | SessionDiscardData
      | TelehealthInstructionsData,
  >(
    type: string
  ) => T | undefined;
  closeDialog: (type: string, result?: unknown) => void;
}

export const DialogRenderer: React.FC<DialogRendererProps> = ({
  activeDialogs,
  getDialogData,
  closeDialog,
}) => {
  const renderDialog = (dialogType: string) => {
    switch (dialogType) {
      case DIALOG_TYPES.EXIT_CONFIRM: {
        const data = getDialogData<ExitConfirmData>(dialogType);
        return (
          <ExitConfirmDialog
            key={dialogType}
            data={data}
            onConfirm={() => {
              const exitAction = data?.action || EXIT_ACTIONS.CLOSE;
              closeDialog(DIALOG_TYPES.EXIT_CONFIRM, {
                confirmed: true,
                action: exitAction,
              });
            }}
            onCancel={() =>
              closeDialog(DIALOG_TYPES.EXIT_CONFIRM, {
                confirmed: false,
              })
            }
          />
        );
      }

      case DIALOG_TYPES.PATIENT_CONSENT: {
        return (
          <PatientConsentDialog
            key={dialogType}
            open={true}
            onConsent={(doNotShowAgain) =>
              closeDialog(DIALOG_TYPES.PATIENT_CONSENT, {
                action: CONSENT_ACTIONS.CONSENT,
                doNotShowAgain,
              })
            }
            onCancel={() =>
              closeDialog(DIALOG_TYPES.PATIENT_CONSENT, {
                action: CONSENT_ACTIONS.CANCEL,
              })
            }
          />
        );
      }
      case DIALOG_TYPES.SESSION_DISCARD: {
        const data = getDialogData<SessionDiscardData>(dialogType);
        return (
          <SessionDiscardDialog
            key={dialogType}
            data={data}
            onConfirm={() =>
              closeDialog(DIALOG_TYPES.SESSION_DISCARD, {
                action: SESSION_DISCARD_ACTIONS.DISCARD,
              })
            }
            onContinue={() =>
              closeDialog(DIALOG_TYPES.SESSION_DISCARD, {
                action: SESSION_DISCARD_ACTIONS.CONTINUE,
              })
            }
            onCancel={() =>
              closeDialog(DIALOG_TYPES.SESSION_DISCARD, {
                action: SESSION_DISCARD_ACTIONS.CANCEL,
              })
            }
          />
        );
      }
      case DIALOG_TYPES.TELEHEALTH_INSTRUCTIONS: {
        return (
          <TelehealthInstructionsDialog
            key={dialogType}
            open={true}
            onContinue={(doNotShowAgain) =>
              closeDialog(DIALOG_TYPES.TELEHEALTH_INSTRUCTIONS, {
                action: TELEHEALTH_INSTRUCTION_ACTIONS.CONTINUE,
                doNotShowAgain,
              })
            }
            onCancel={() =>
              closeDialog(DIALOG_TYPES.TELEHEALTH_INSTRUCTIONS, {
                action: TELEHEALTH_INSTRUCTION_ACTIONS.CANCEL,
              })
            }
          />
        );
      }
      default:
        logWarn(
          'DialogRenderer',
          `No dialog component registered for type: ${dialogType}`
        );
        return null;
    }
  };

  return (
    <>
      {Array.from(activeDialogs.keys()).map((dialogType) =>
        renderDialog(dialogType)
      )}
    </>
  );
};
